<template>
  <div v-if="subcategoryTabData && subcategoryTabData.length">
    <h2 class="main-subtitle" :class="subcategoryTabTypeClass">
      <LockIcon :published-to="props.subcategory.publishedTo" />
      {{ subcategoryTabTitle }}
    </h2>
    <product-block v-for="(item, index) in subcategoryTabDataCollected" :key="index" :productInfo="item" />
  </div>
  <div v-else class="ta-c">No data found for subcategory tab</div>
</template>

<script setup lang="ts">
  import { useRoute } from 'vue-router';
  import { useMainStore } from '@/stores/main';
  import { setProperFieldExternalFirst } from '@/utils/fieldGetters';
  import ProductBlock from '~/components/product/ProductBlock.vue';
  import { privacyTypes } from '~/constants';

  interface Node {
    nodeId: string | number;
    id?: string | number;
    external?: {
      title?: string;
      [key: string]: any;
    };
    nodes?: Node[];
    [key: string]: any;
  }

  interface Subcategory {
    nodes?: Node[];
    [key: string]: any;
  }

  definePageMeta({ middleware: ['check-channel-nodes', 'check-token'] });

  const route = useRoute();
  const mainStore = useMainStore();
  const props = defineProps<{
    subcategory: Subcategory;
  }>();

  // Make route params reactive
  const currentCategory = computed(() => (Array.isArray(route.params.category) ? route.params.category[0] : route.params.category));
  const currentSubcategory = computed(() =>
    Array.isArray(route.params.subcategory) ? route.params.subcategory[0] : route.params.subcategory
  );
  const currentSubcategoryTab = computed(() =>
    Array.isArray(route.params.subcategoryTab) ? route.params.subcategoryTab[0] : route.params.subcategoryTab
  );

  // Ensure data is loaded server/client-side
  async function ensureChannelNodes() {
    if (mainStore.channelNodes && mainStore.channelNodes.length) return;
    const categories = Array.isArray(mainStore.categories) ? mainStore.categories : [];
    const cat = categories.find(
      (c) => String(c.nodeId) === String(currentCategory.value) || String(c.id) === String(currentCategory.value)
    );
    const sub =
      cat && Array.isArray(cat.nodes)
        ? cat.nodes.find((s) => String(s.nodeId) === String(currentSubcategory.value) || String(s.id) === String(currentSubcategory.value))
        : null;
    const tab =
      sub && Array.isArray(sub.nodes)
        ? sub.nodes.find(
            (t) => String(t.nodeId) === String(currentSubcategoryTab.value) || String(t.id) === String(currentSubcategoryTab.value)
          )
        : null;
    if (tab && Array.isArray(tab.nodes) && tab.nodes.length) {
      const ids = tab.nodes
        .map((n) => n.id ?? n.nodeId)
        .filter(Boolean)
        .join(',');
      mainStore.channelNodes = ids;
    }
  }

  // Load initial data before useAsyncData (so immediate: false works)
  if (route.path && route.path.startsWith('/products')) {
    if (!Array.isArray(mainStore.categories) || mainStore.categories.length === 0) {
      await mainStore.serverInit({ route, params: route.params });
    }
    await ensureChannelNodes();
    await mainStore.setSubcategoryTabData({ params: route.params, checkData: { check: false } });
  }

  // Initialize data with reactive key - auto-refetches when params change
  const { data: asyncData } = await useAsyncData(
    () => `subcategoryTabData-${currentCategory.value}-${currentSubcategory.value}-${currentSubcategoryTab.value}`,
    async () => {
      if (route.path && route.path.startsWith('/products')) {
        await ensureChannelNodes();
        await mainStore.setSubcategoryTabData({ params: route.params, checkData: { check: false } });
        return mainStore.subcategoryTabData;
      } else return [];
    },
    {
      watch: [currentSubcategoryTab],
      immediate: false // Prevents duplicate call on mount
    }
  );

  const subcategoryTabData = computed(() => mainStore.subcategoryTabData);

  let cachedSubcategoryTabIndex = -1;

  const subcategoryTabIndex = computed(() => {
    if (!props.subcategory.nodes) return cachedSubcategoryTabIndex;
    const index = props.subcategory.nodes.findIndex((node: Node) => node.nodeId == currentSubcategoryTab.value);
    if (index === -1) {
      return cachedSubcategoryTabIndex;
    }
    cachedSubcategoryTabIndex = index;
    return index;
  });

  const subcategoryTabTitle = computed(() => {
    if (props.subcategory.nodes && subcategoryTabIndex.value >= 0 && props.subcategory.nodes[subcategoryTabIndex.value]) {
      return setProperFieldExternalFirst(props.subcategory.nodes[subcategoryTabIndex.value], 'title');
    }
    return '';
  });

  // Heading-level internal/service modifier — draws the borderline segment that
  // stitches the card's top edge to the first section's rail (DOV-1488 task 1).
  // Reads the same source as the heading lock so the two always agree.
  const subcategoryTabTypeClass = computed(() => {
    const subcategoryPublishedTo = Number(props.subcategory.publishedTo);
    const publishedTo =
      subcategoryPublishedTo === privacyTypes.INTERNAL || subcategoryPublishedTo === privacyTypes.SERVICE
        ? subcategoryPublishedTo
        : Number(subcategoryTabDataCollected.value[0]?.publishedTo);
    switch (publishedTo) {
      case privacyTypes.INTERNAL:
        return 'main-subtitle_internal';
      case privacyTypes.SERVICE:
        return 'main-subtitle_service';
      default:
        return '';
    }
  });

  const subcategoryTabDataCollected = computed(() => {
    const productsArr: any[] = [];
    const transformedArr: any[] = [];
    const productCard = {
      items: [] as any[],
      productBlockType: 'ProductCard'
    };
    const productOverview = {
      items: [] as any[],
      productBlockType: 'ProductOverview'
    };
    const productBlank = {
      external: { title: '' },
      items: [] as any[],
      id: null as string | number | null,
      productBlockType: ''
    };

    // if we have channelNodes we add them to our result
    let channelNodes: Node[] | null = null;
    if (props.subcategory && props.subcategory.nodes && props.subcategory.nodes.length && subcategoryTabIndex.value > -1) {
      channelNodes = props.subcategory.nodes[subcategoryTabIndex.value]
        ? props.subcategory.nodes[subcategoryTabIndex.value].nodes || null
        : null;
    }
    const isChannelNodesPresent = channelNodes ? channelNodes.length > 0 : false;

    if (isChannelNodesPresent && channelNodes) {
      for (const channelNode of channelNodes) {
        const copy = Object.assign({}, productBlank);
        copy.id = channelNode.id || channelNode.nodeId || null;
        copy.external.title = channelNode.external?.title || '';
        productsArr.push(copy);
      }
    }

    // adding ProductBlocks by their type to our blank elements
    for (const productBlock of subcategoryTabData.value || []) {
      if (productBlock.productBlockType == 'ProductOverview') {
        if (isChannelNodesPresent) {
          const index = productsArr.findIndex((productOverview) => productOverview.id === productBlock.parentId);
          if (index == -1) {
            productOverview.items.push(productBlock);
          } else {
            productsArr[index].productBlockType = 'ProductOverview';
            productsArr[index].items.push(productBlock);
          }
        } else {
          productOverview.items.push(productBlock);
        }
      } else if (productBlock.productBlockType == 'ProductCard') {
        if (isChannelNodesPresent) {
          const index = productsArr.findIndex((productCard) => productCard.id === productBlock.parentId);
          if (index == -1) {
            productCard.items.push(productBlock);
          } else {
            productsArr[index].productBlockType = 'ProductCard';
            productsArr[index].items.push(productBlock);
          }
        } else {
          productCard.items.push(productBlock);
        }
      } else {
        transformedArr.push(productBlock);
      }
    }

    // adding transformed blank elements to our real data
    const productCardsArr = productsArr.filter((productBlock) => productBlock.productBlockType == 'ProductCard');
    const productOverviewsArr = productsArr.filter((productBlock) => productBlock.productBlockType == 'ProductOverview');

    if (productCardsArr.length > 0) transformedArr.push(...productCardsArr);
    if (productCard.items.length > 0) transformedArr.push(productCard);
    if (productOverviewsArr.length > 0) transformedArr.push(...productOverviewsArr);
    if (productOverview.items.length > 0) transformedArr.push(productOverview);
    return transformedArr;
  });

  // TODO: Replace tab/sidebar state logic with Pinia if needed
</script>
